/**
 * The persisted shape: the full column ORDER plus the set of HIDDEN ids — never
 * the set of visible ones. A positive list silently hides every column added
 * later, and nobody notices, because nothing that WAS there has gone.
 */
export type ColumnPreference = {
  readonly order: ReadonlyArray<string>;
  readonly hidden: ReadonlyArray<string>;
};

export const EMPTY_COLUMN_PREFERENCE: ColumnPreference = { order: [], hidden: [] };

export type ResolvedColumnOrder = {
  /** Every catalogue id, in the member's order — hidden ones included, in place. */
  readonly order: ReadonlyArray<string>;
  readonly hidden: ReadonlySet<string>;
  /** Catalogue ids the stored order did not carry: new since the member last saved. */
  readonly added: ReadonlySet<string>;
};

/**
 * The stored value is arbitrary JSON the server never validates, so every field
 * is re-derived rather than trusted — an older bundle, a hand-written row, or a
 * key collision must degrade to defaults instead of throwing inside a render.
 */
export function parseColumnPreference(value: unknown): ColumnPreference {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return EMPTY_COLUMN_PREFERENCE;

  const record = value as { readonly order?: unknown; readonly hidden?: unknown };

  return { order: stringIds(record.order), hidden: stringIds(record.hidden) };
}

function stringIds(value: unknown): ReadonlyArray<string> {
  if (!Array.isArray(value)) return [];

  // De-duplicated: a repeated id in the stored order would render its column twice.
  return [...new Set(value.filter((entry): entry is string => typeof entry === 'string'))];
}

/**
 * `lockedLeadingIds` is a block the table pins: it always leads, in catalogue order, and is
 * never hidden or „neu", whatever the stored blob says. `defaultHiddenIds` start hidden until
 * the stored order places them: once a write has placed a column, the stored `hidden` decides.
 */
export function resolveColumnOrder(
  catalogue: ReadonlyArray<string>,
  alwaysVisibleId: string,
  persisted: ColumnPreference,
  lockedLeadingIds: ReadonlyArray<string> = [],
  defaultHiddenIds: ReadonlyArray<string> = [],
): ResolvedColumnOrder {
  const known = new Set(catalogue);
  // An id the catalogue no longer has is dropped, not carried: a removed column
  // must not hold a position in every member's order for ever.
  const stored = persisted.order.filter((id) => known.has(id));
  const storedIds = new Set(stored);
  const appended = catalogue.filter((id) => !storedIds.has(id));
  // Nothing is "new" to a member who has never saved, or their first visit after
  // this ships would badge every column.
  const configured = stored.length > 0;
  const locked = new Set(lockedLeadingIds);
  const lead = catalogue.filter((id) => locked.has(id));
  // Normalised, never trusted: a hand-edited blob that put a locked id elsewhere is corrected
  // here, and every write derives from this order.
  const rest = (configured ? [...stored, ...appended] : [...catalogue]).filter((id) => !locked.has(id));

  const startHidden = defaultHiddenIds.filter(
    (id) => !storedIds.has(id) && known.has(id) && id !== alwaysVisibleId && !locked.has(id),
  );

  return {
    order: [...lead, ...rest],
    // The always-visible column can never be hidden, whatever the blob says.
    hidden: new Set([
      ...persisted.hidden.filter((id) => known.has(id) && id !== alwaysVisibleId && !locked.has(id)),
      ...startHidden,
    ]),
    // A locked column sits at the front, where it always has, so it is never „neu".
    added: new Set(configured ? appended.filter((id) => !locked.has(id)) : []),
  };
}

/**
 * Moves `id` past its nearest VISIBLE neighbour in `direction`, carrying it over
 * any hidden columns in between. Stepping one index at a time would read as a
 * broken command: a hidden neighbour is not on screen, so the table would not
 * change and the member would press again. Returns the SAME array reference when
 * the move is impossible, which is also how the call site derives `disabled`.
 */
export function moveColumn(
  order: ReadonlyArray<string>,
  hidden: ReadonlySet<string>,
  id: string,
  direction: 'left' | 'right',
): ReadonlyArray<string> {
  const from = order.indexOf(id);

  if (from === -1) return order;

  const step = direction === 'left' ? -1 : 1;
  let to = from + step;

  while (to >= 0 && to < order.length && hidden.has(order[to] as string)) to += step;

  if (to < 0 || to >= order.length) return order;

  const next = [...order];
  next.splice(from, 1);
  next.splice(to, 0, id);

  return next;
}

export function canMove(
  order: ReadonlyArray<string>,
  hidden: ReadonlySet<string>,
  id: string,
  direction: 'left' | 'right',
): boolean {
  return moveColumn(order, hidden, id, direction) !== order;
}

/**
 * Takes the target state rather than toggling, so the checkbox's own boolean
 * decides and a double event can never leave the control and the store
 * disagreeing about which way "toggle" went.
 */
export function setColumnVisible(hidden: ReadonlySet<string>, id: string, visible: boolean): ReadonlyArray<string> {
  const next = new Set(hidden);

  if (visible) next.delete(id);
  else next.add(id);

  return [...next];
}

/**
 * Moves `id` so that it sits immediately before `beforeId`, or at the end when
 * `beforeId` is null. Defined on the FULL order (hidden columns included), so a
 * hidden column keeps its place whichever surface the drag came from.
 *
 * Returns the SAME array reference when the drop changes nothing — the drop onto
 * the column's own position, and the drop onto the neighbour it already precedes.
 * The call site uses the identity to skip the write rather than persisting a no-op.
 */
export function reorderColumn(
  order: ReadonlyArray<string>,
  id: string,
  beforeId: string | null,
): ReadonlyArray<string> {
  const from = order.indexOf(id);

  if (from === -1 || beforeId === id) return order;

  const without = order.filter((entry) => entry !== id);
  const to = beforeId === null ? without.length : without.indexOf(beforeId);

  if (to === -1) return order;

  // `from` indexes `order`, `to` indexes `without`: removing `id` shifts later
  // entries down one, so equality means `id` already sits right before `beforeId`.
  if (to === from) return order;

  return [...without.slice(0, to), id, ...without.slice(to)];
}

/**
 * `fullOrder` with the slots held by `subsetOrder`'s ids refilled in `subsetOrder`'s
 * sequence; every other id keeps its slot. Returns the SAME reference when nothing moved.
 */
export function mergeSubsetOrder(
  fullOrder: ReadonlyArray<string>,
  subsetOrder: ReadonlyArray<string>,
): ReadonlyArray<string> {
  const members = new Set(subsetOrder);
  let next = 0;
  const merged = fullOrder.map((id) => (members.has(id) ? (subsetOrder[next++] as string) : id));

  return merged.every((id, index) => id === fullOrder[index]) ? fullOrder : merged;
}
